import { Autocomplete, TextField, Typography } from '@mui/material'
import groupBy from 'lodash/groupBy'
import keyBy from 'lodash/keyBy'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { MyAssigneeGroups } from '~/entities/assignee-group'
import { required } from '~/shared/lib/required'

type OwnerGroupOption = {
  label: string
  name: string
  value: number | undefined
  practicingStates: string | undefined
}

const OwnerGroupFilter = ({
  value,
  onChange,
  label,
  cssClass,
  groupOnly,
  myGroups,
}: {
  value?: number[]
  onChange: (value?: number[]) => void
  label?: string
  cssClass?: string
  groupOnly?: boolean
  /**
   * The caller's own memberships. When provided, they are listed first under Me / Covering / My
   * teams and removed from the long provider sections below, so the groups someone actually works
   * are the first thing in the list instead of being buried among every provider.
   */
  myGroups?: MyAssigneeGroups
}) => {
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)

  // Listed first, in the order someone reaches for them: their own queue, then who they're
  // covering, then their teams. A covering entry reads as the covered person's name.
  const myGroupOptions = useMemo<OwnerGroupOption[]>(() => {
    if (!myGroups) return []
    const entries = myGroups.personal
      ? [
          {
            label: 'ME',
            name: myGroups.personal.name,
            value: myGroups.personal.id,
            practicingStates: '',
          },
        ]
      : []
    return entries.concat(
      myGroups.covering.map(group => ({
        label: 'COVERING',
        name: group.owner?.name ?? group.name,
        value: group.id,
        practicingStates: '',
      })),
      myGroups.teams.map(group => ({
        label: 'MY TEAMS',
        name: group.name,
        value: group.id,
        practicingStates: '',
      })),
    )
  }, [myGroups])

  const { groupedOptions } = useMemo(() => {
    const providerTypeByID = providers.reduce<Record<number, 'Clinicians' | 'Other'>>(
      (lookup, provider) => {
        if (provider.provider_fields?.practicing_states) {
          lookup[provider.id] = 'Clinicians'
          return lookup
        }

        lookup[provider.id] = 'Other'
        return lookup
      },
      {},
    )

    const grouped = groupBy(providers, provider => providerTypeByID[provider.id])
    const groupedClinicians = grouped.Clinicians
      ? grouped.Clinicians.map(provider => ({
          label: 'Clinical',
          name: `${provider.assignee_group?.name}`,
          value: provider.assignee_group?.id,
          practicingStates: provider.provider_fields?.practicing_states
            ?.map(location => location.abbreviation)
            .join(', '),
        }))
      : []
    const groupedOther = grouped.Other
      ? grouped.Other.map(provider => ({
          label: 'Other',
          name: `${provider.assignee_group?.name}`,
          value: provider.assignee_group?.id,
          practicingStates: provider.provider_fields?.practicing_states
            ?.map(location => location.abbreviation)
            .join(', '),
        }))
      : []

    // Retired groups are not offered as filters. One already persisted in the URL still renders:
    // the placeholder-option fallback below covers ids missing from the derived options.
    const orderedGroupOfUsers = [...(assigneeGroups || [])]
      .filter(group => !group.is_deprecated)
      .sort((a, b) => a.name.localeCompare(b.name))
    const groupedGroupOfUsers = orderedGroupOfUsers
      ? orderedGroupOfUsers.map(group => ({
          label: 'GROUPS',
          name: `${group.name}`,
          value: group.id,
          practicingStates: '',
        }))
      : []
    let groupedOptions: OwnerGroupOption[]
    if (groupOnly) {
      groupedOptions = groupedGroupOfUsers
    } else {
      groupedOptions = groupedClinicians
      groupedOptions = groupedOptions.concat(groupedOther, groupedGroupOfUsers)
    }

    // Each group appears once: a group listed under Me / Covering / My teams is dropped from the
    // provider and GROUPS sections rather than shown twice.
    if (myGroupOptions.length) {
      const myGroupIds = new Set(myGroupOptions.map(option => option.value))
      groupedOptions = myGroupOptions.concat(
        groupedOptions.filter(option => !myGroupIds.has(option.value)),
      )
    }

    return { groupedOptions }
  }, [providers, assigneeGroups, myGroupOptions, groupOnly])

  const providerGroupsByID = keyBy(groupedOptions, 'value')

  // A selected group can be absent from the derived options — e.g. a group
  // persisted in the URL filter whose provider isn't in the loaded `providers`
  // set. Represent those as placeholder options so the Autocomplete value
  // reference-matches an option: otherwise MUI logs "the value provided is
  // invalid" and the selection renders as a blank chip.
  const selectedIds = value ?? []
  const placeholderOptions = selectedIds
    .filter(id => !providerGroupsByID[id])
    .map(id => ({
      label: 'GROUPS',
      // A retired group is left out of the options above but is still in the loaded state, so a
      // filter that already holds one renders its name instead of an opaque id.
      name: assigneeGroups?.find(group => group.id === id)?.name ?? `Group ${id}`,
      value: id,
      practicingStates: '',
    }))
  const options = placeholderOptions.length
    ? groupedOptions.concat(placeholderOptions)
    : groupedOptions
  const optionsByID = placeholderOptions.length
    ? { ...providerGroupsByID, ...keyBy(placeholderOptions, 'value') }
    : providerGroupsByID

  return (
    <Autocomplete
      size="small"
      multiple
      options={options}
      groupBy={provider => provider.label}
      getOptionLabel={provider => provider.name}
      filterSelectedOptions
      renderInput={params => <TextField {...params} label={label} />}
      isOptionEqualToValue={(option, value) => option.value == value.value}
      classes={{
        input: cssClass ? cssClass : '',
        option: cssClass ? cssClass : '',
      }}
      sx={{
        '.MuiInputLabel-root.MuiFormLabel-root': {
          fontSize: cssClass ? '1.4rem' : '1.6rem',
          zIndex: cssClass ? '0' : '1',
        },
      }}
      renderOption={({ key: _, ...props }, provider) => (
        <li {...props} key={provider.value}>
          <span>
            {provider.name || 'Unknown Provider'}
            {provider.practicingStates && (
              <Typography
                className={cssClass ? cssClass : ''}
                sx={{ color: 'secondary.main', display: 'inline' }}
              >
                {'  '}
                {provider.practicingStates}
              </Typography>
            )}
          </span>
        </li>
      )}
      value={selectedIds.map(id => required(optionsByID[id], `an option for group ${id}`))}
      onChange={(_, selected) => {
        onChange(selected.map(provider => provider.value ?? 0))
      }}
      disableCloseOnSelect
    />
  )
}

export default OwnerGroupFilter
